// Shopify renders result HTML. This component only coordinates the dialog and requests.
if (!customElements.get('szponty-search')) {
  customElements.define('szponty-search', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.dialog = this.querySelector('dialog');
      if (typeof this.dialog?.showModal !== 'function') return;
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.results = this.querySelector('#SearchPredictions');
      this.status = this.querySelector('#SearchHint');
      this.all = this.querySelector('[data-search-all]');
      const on = (target, type, handler) => target.addEventListener(type, handler, { signal: this.events.signal });
      const enhance = () => document.querySelectorAll('[data-search-open]').forEach(link => {
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', this.dialog.id);
        link.setAttribute('aria-expanded', String(this.dialog.open));
      });
      enhance();
      on(document, 'shopify:section:load', enhance);
      on(document, 'click', event => {
        const opener = event.target.closest('[data-search-open]');
        if (!opener || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        document.querySelectorAll('storefront-navigation details[open]').forEach(details => { details.open = false; });
        this.opener = opener;
        this.dialog.showModal();
        this.trackViewport();
        this.dialog.scrollTop = 0;
        enhance();
        this.input.focus({ preventScroll: true });
        this.input.select();
        this.query = null;
        this.queue();
      });
      on(this.querySelector('[data-search-close]'), 'click', () => this.dialog.close());
      on(this.dialog, 'close', () => {
        this.cancel();
        this.stopViewport();
        enhance();
        const opener = this.opener?.isConnected ? this.opener : document.querySelector('[data-search-open]');
        opener?.focus({ preventScroll: true });
      });
      on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      });
      on(this.input, 'input', event => {
        this.input.setCustomValidity('');
        if (!event.isComposing) this.queue();
      });
      on(this.input, 'compositionstart', () => { this.cancel(); this.query = null; });
      on(this.input, 'compositionend', () => this.queue());
      on(this.form, 'submit', event => {
        const query = this.input.value.trim();
        if (!/[\p{L}\p{N}]/u.test(query)) {
          event.preventDefault();
          this.input.setCustomValidity(this.dataset.hint);
          this.input.reportValidity();
        } else {
          this.input.value = query;
          this.cancel();
        }
      });
      on(this.dialog, 'keydown', event => this.keydown(event));
    }

    trackViewport() {
      this.stopViewport();
      const viewport = window.visualViewport;
      if (!viewport) return;
      this.viewportEvents = new AbortController();
      const update = () => {
        this.dialog.style.setProperty('--search-viewport-height', viewport.height + 'px');
        this.dialog.style.setProperty('--search-viewport-top', viewport.offsetTop + 'px');
      };
      for (const type of ['resize', 'scroll']) viewport.addEventListener(type, update, { signal: this.viewportEvents.signal });
      update();
    }

    stopViewport() {
      this.viewportEvents?.abort();
      this.dialog?.style.removeProperty('--search-viewport-height');
      this.dialog?.style.removeProperty('--search-viewport-top');
    }

    cancel() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.version = (this.version || 0) + 1;
      this.results?.setAttribute('aria-busy', 'false');
    }

    queue() {
      const query = this.input.value.trim().replace(/\s+/g, ' ');
      if (query === this.query) return;
      this.query = query;
      this.cancel();
      this.results.replaceChildren();
      this.status.textContent = this.dataset.hint;
      const characters = query.match(/\p{L}/gu) || [];
      this.all.hidden = characters.length === 0;
      const url = new URL(this.form.action);
      url.search = new URLSearchParams(new FormData(this.form));
      url.searchParams.set('q', query);
      this.all.href = url.href;
      if (characters.length < 3 || !this.dialog.open) return;
      this.timer = setTimeout(() => this.predict(query, this.version), 250);
    }

    async predict(query, version) {
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      const url = new URL(this.dataset.predictiveUrl, location.origin);
      url.search = new URLSearchParams({
        q: query, section_id: 'predictive-search',
        'resources[type]': 'product,collection,page',
        'resources[limit]': '4', 'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last'
      });
      this.status.textContent = this.dataset.loading;
      this.results.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Predictive search unavailable');
        const text = await response.text();
        if (version !== this.version || !this.dialog.open) return;
        const html = new DOMParser().parseFromString(text, 'text/html');
        const content = html.querySelector('[data-predictive-content]');
        if (!content) throw new Error('Predictive search unavailable');
        if (version !== this.version || !this.dialog.open) return;
        this.results.replaceChildren(content);
        this.status.textContent = content.dataset.summary;
      } catch {
        if (version === this.version && this.dialog.open) this.status.textContent = this.dataset.error;
      } finally {
        clearTimeout(timeout);
        if (version === this.version) this.results.setAttribute('aria-busy', 'false');
      }
    }

    keydown(event) {
      // Search inputs can consume Escape to clear text; close the panel on that first press.
      if (event.key === 'Escape' && !event.isComposing) {
        event.preventDefault();
        this.dialog.close();
        return;
      }
      // Native dialog supplies background inertness. Keep Tab inside it.
      const focusable = [...this.dialog.querySelectorAll('a[href], button, input:not([type="hidden"])')]
        .filter(element => !element.disabled && element.getClientRects().length);
      if (event.key === 'Tab') {
        const first = focusable[0], last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
      if (event.isComposing || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const links = [...this.results.querySelectorAll('a[href]')];
      if (!links.length) return;
      if (event.target === this.input) {
        event.preventDefault();
        (event.key === 'ArrowDown' ? links[0] : links.at(-1)).focus();
      } else if (links.includes(event.target)) {
        event.preventDefault();
        const next = links.indexOf(event.target) + (event.key === 'ArrowDown' ? 1 : -1);
        (links[next] || this.input).focus();
      }
    }

    disconnectedCallback() {
      this.cancel();
      if (this.dialog?.open) this.dialog.close();
      this.stopViewport();
      this.events?.abort();
    }
  });
}
